import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { debounce } from 'lodash';
import './AdminPanel.css';

const API_URL = 'https://zangena-e33a7e55637a.herokuapp.com/api';

const AdminPanel = () => {
  const [tab, setTab] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalUserBalance: 0,
    pendingUserDepositsCount: 0,
    pendingUserWithdrawalsCount: 0,
    totalBusinesses: 0,
    totalBusinessBalance: 0,
    pendingBusinessDepositsCount: 0,
    pendingBusinessWithdrawalsCount: 0,
    totalBalance: 0,
    recentTxCount: 0,
  });
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [entityType, setEntityType] = useState('businesses');
  const token = localStorage.getItem('token');

  const axiosInstance = useMemo(() => axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 15000,
  }), [token]);

  const fetchWithRetry = useCallback(async (url, options = {}, retries = 3, baseDelay = 2000) => {
    for (let i = 0; i < retries; i++) {
      try {
        console.log(`[FETCH] GET ${url} - Attempt ${i + 1}`);
        const response = await axiosInstance.get(url, options);
        console.log(`[FETCH] Success for ${url}`, response.data);
        return response;
      } catch (err) {
        console.error(`[FETCH] Error on ${url} (Attempt ${i + 1})`, {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
        });
        if (i === retries - 1) throw err;
        const delay = baseDelay * Math.pow(2, i);
        console.log(`Retry ${i + 1}/${retries} for ${url} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }, [axiosInstance]);

  const validateToken = useCallback(async () => {
    try {
      console.log('[ValidateToken] Checking token with /users/check');
      const response = await axiosInstance.get('/users/check');
      console.log('[ValidateToken] Token is valid', response.data);
      return true;
    } catch (err) {
      console.error('[ValidateToken] Token validation failed', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      if (err.response?.status === 404) {
        setError('Token validation endpoint not found. Contact support@fugipay.com.');
      }
      return false;
    }
  }, [axiosInstance]);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setIsAuthenticated(false);
        setError('Please log in to access the admin panel.');
        return;
      }
      const isValid = await validateToken();
      setIsAuthenticated(isValid);
      if (!isValid) {
        setError('Invalid or expired token. Redirecting to sign-in...');
        setTimeout(() => {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          window.location.href = '/signin';
        }, 5000);
      }
    };
    checkAuth();
  }, [token, validateToken]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !token) return;
      setError('');
      setLoading(true);
      try {
        if (tab === 'dashboard') {
          const { data } = await fetchWithRetry('/admin/stats');
          console.log('[Dashboard] Stats Response:', data);
          setStats({
            totalUsers: Number(data.totalUsers) || 0,
            totalUserBalance: Number(data.totalUserBalance) || 0,
            pendingUserDepositsCount: Number(data.pendingUserDepositsCount) || 0,
            pendingUserWithdrawalsCount: Number(data.pendingUserWithdrawalsCount) || 0,
            totalBusinesses: Number(data.totalBusinesses) || 0,
            totalBusinessBalance: Number(data.totalBusinessBalance) || 0,
            pendingBusinessDepositsCount: Number(data.pendingBusinessDepositsCount) || 0,
            pendingBusinessWithdrawalsCount: Number(data.pendingBusinessWithdrawalsCount) || 0,
            totalBalance: Number(data.totalBalance) || 0,
            recentTxCount: Number(data.recentTxCount) || 0,
          });
        } else if (tab === 'users') {
          const { data } = await axiosInstance.get('/admin/users', {
            params: { page, limit: 10, search },
          });
          console.log('[Users] Response:', data);
          setUsers(Array.isArray(data.users) ? data.users : []);
          setTotalPages(Math.ceil(data.total / 10) || 1);
        } else if (tab === 'businesses') {
          const { data } = await axiosInstance.get('/admin/businesses', {
            params: { page, limit: 10, search },
          });
          console.log('[Businesses] Response:', data);
          setBusinesses(Array.isArray(data.businesses) ? data.businesses : []);
          setTotalPages(data.pages || 1);
        } else if (tab === 'deposits' || tab === 'withdrawals') {
          const endpoint = entityType === 'businesses' ? '/admin/businesses' : '/admin/users';
          const { data } = await axiosInstance.get(endpoint, {
            params: { page, limit: 10, search, isActive: true },
          });
          console.log(`[${entityType}] Response:`, data);
          const entities = entityType === 'businesses' ? data.businesses : data.users;
          if (!Array.isArray(entities)) {
            throw new Error(`Invalid ${entityType} response structure`);
          }

          if (tab === 'deposits') {
            const depositRequests = entities
              .flatMap((entity) => {
                if (!entity.pendingDeposits || !Array.isArray(entity.pendingDeposits)) {
                  console.log(`[Deposits] No pendingDeposits for ${entity._id}`);
                  return [];
                }
                return entity.pendingDeposits
                  .map((d, index) => {
                    if (!d.amount || !d.transactionId) {
                      console.log(`[Deposits] Invalid deposit for ${entity._id}`, d);
                      return null;
                    }
                    const amount = typeof d.amount === 'object' && d.amount.$numberDecimal
                      ? parseFloat(d.amount.$numberDecimal)
                      : parseFloat(d.amount?.toString() || '0');
                    return {
                      id: entity._id,
                      businessId: entityType === 'businesses' ? entity.businessId : null,
                      transactionId: d.transactionId,
                      index: index,
                      name: entityType === 'businesses' ? entity.name : entity.username,
                      entityId: entityType === 'businesses' ? entity.businessId : entity.username,
                      amount: isNaN(amount) ? 0 : amount,
                      currency: d.currency || 'ZMW',
                      status: d.status?.trim().toLowerCase() || 'pending',
                      date: d.date || new Date().toISOString(),
                      sourceOfFunds: d.sourceOfFunds || 'N/A',
                    };
                  })
                  .filter(d => d !== null && d.status === 'pending');
              });
            console.log('[Deposit Requests]', depositRequests);
            setDeposits(depositRequests);
            setTotalPages(Math.ceil(depositRequests.length / 10) || 1);
            if (depositRequests.length === 0) {
              setError(`No pending ${entityType} deposit requests available.`);
            }
          }

          if (tab === 'withdrawals') {
            const withdrawalRequests = entities
              .flatMap((entity) => {
                if (!entity.pendingWithdrawals || !Array.isArray(entity.pendingWithdrawals)) {
                  console.log(`[Withdrawals] No pendingWithdrawals for ${entity._id}`);
                  return [];
                }
                return entity.pendingWithdrawals
                  .map((w, index) => {
                    if (!w.amount) {
                      console.log(`[Withdrawals] Invalid withdrawal for ${entity._id}`, w);
                      return null;
                    }
                    console.log(`[Withdrawals] Processing withdrawal for ${entity._id}`, w);
                    const amount = typeof w.amount === 'object' && w.amount.$numberDecimal
                      ? parseFloat(w.amount.$numberDecimal)
                      : parseFloat(w.amount?.toString() || '0');
                    const fee = entityType === 'businesses'
                      ? (typeof w.fee === 'object' && w.fee.$numberDecimal
                          ? parseFloat(w.fee.$numberDecimal)
                          : parseFloat(w.fee?.toString() || '0'))
                      : 0;
                    return {
                      id: entity._id,
                      businessId: entityType === 'businesses' ? entity.businessId : null,
                      index: index,
                      name: entityType === 'businesses' ? entity.name : entity.username,
                      entityId: entityType === 'businesses' ? entity.businessId : entity.username,
                      amount: isNaN(amount) ? 0 : amount,
                      fee: isNaN(fee) ? 0 : fee,
                      currency: entityType === 'businesses' ? (w.currency || 'ZMW') : 'ZMW',
                      status: w.status?.trim().toLowerCase() || 'pending',
                      date: w.date || new Date().toISOString(),
                      destination: entityType === 'businesses' ? {
                        type: w.destination?.type || 'unknown',
                        accountDetails: (
                          w.destination?.type === 'bank'
                            ? `${w.destination?.bankName || 'N/A'} (${w.destination?.accountNumber || 'N/A'}) ${w.destination?.swiftCode || ''}`
                            : w.destination?.accountNumber || 'N/A'
                        ).trim(),
                      } : {
                        type: 'unknown',
                        accountDetails: 'N/A',
                      },
                    };
                  })
                  .filter(w => w !== null && w.status === 'pending');
              });
            console.log('[Withdrawal Requests]', withdrawalRequests);
            setWithdrawals(withdrawalRequests);
            setTotalPages(Math.ceil(withdrawalRequests.length / 10) || 1);
            if (withdrawalRequests.length === 0) {
              setError(`No pending ${entityType} withdrawal requests available.`);
            }
          }
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tab, isAuthenticated, token, page, search, entityType, axiosInstance, fetchWithRetry]);

  const debouncedSearch = useCallback(debounce((value) => {
    setSearch(value);
    setPage(1);
  }, 500), []);

  const handleSearch = (e) => debouncedSearch(e.target.value);

  const updateUserKYC = async (id, kycStatus) => {
    try {
      console.log(`[UpdateUserKYC] Updating user: ${id}, kycStatus: ${kycStatus}`);
      const response = await axiosInstance.post('/admin/update-kyc', { id, kycStatus });
      console.log(`[UpdateUserKYC] Success for user: ${id}`, response.data);
      setUsers(users.map(u => u._id === id ? { ...u, kycStatus, isActive: kycStatus === 'verified' } : u));
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to update user KYC: ${err.message}`;
      console.error(`[UpdateUserKYC] Error for user: ${id}`, {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setError(errorMessage);
    }
  };

  const toggleUserActive = async (id) => {
    try {
      console.log(`[ToggleUserActive] Toggling user: ${id}`);
      const response = await axiosInstance.put(`/admin/users/${id}/toggle-active`);
      console.log(`[ToggleUserActive] Success for user: ${id}`, response.data);
      setUsers(users.map(u => u._id === id ? { ...u, isActive: !u.isActive } : u));
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to toggle user status: ${err.message}`;
      console.error(`[ToggleUserActive] Error for user: ${id}`, err);
      setError(errorMessage);
    }
  };

  const refreshBusinesses = async () => {
    try {
      const { data } = await axiosInstance.get('/admin/businesses', {
        params: { page, limit: 10, search },
      });
      console.log('[Refresh Businesses] Response:', data);
      setBusinesses(Array.isArray(data.businesses) ? data.businesses : []);
      setTotalPages(data.pages || 1);
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to refresh businesses: ${err.message}`;
      console.error('[RefreshBusinesses] Error:', err);
      setError(errorMessage);
    }
  };

  const updateBusinessKYC = async (businessId, kycStatus) => {
    try {
      console.log(`[UpdateBusinessKYC] Attempting to update KYC for businessId: ${businessId}, status: ${kycStatus}`);
      const approved = kycStatus === 'verified';
      const response = await axiosInstance.post('/admin/verify-kyc', {
        businessId,
        approved,
        rejectionReason: approved ? null : 'KYC verification failed.',
      });
      console.log(`[UpdateBusinessKYC] Success for businessId: ${businessId}`, response.data);
      setBusinesses(businesses.map(b => b.businessId === businessId ? { ...b, kycStatus, isActive: approved } : b));
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to update business KYC: ${err.message}`;
      console.error(`[UpdateBusinessKYC] Error for businessId: ${businessId}`, {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      if (err.response?.status === 404) {
        setError(`Business with ID ${businessId} not found. Refreshing business list...`);
        await refreshBusinesses();
      } else {
        setError(errorMessage);
      }
    }
  };

  const toggleBusinessActive = async (businessId) => {
    try {
      console.log(`[ToggleBusinessActive] Toggling active status for businessId: ${businessId}`);
      const business = businesses.find(b => b.businessId === businessId);
      const response = await axiosInstance.post('/admin/set-business-active', {
        businessId,
        isActive: !business.isActive,
      });
      console.log(`[ToggleBusinessActive] Success for businessId: ${businessId}`, response.data);
      setBusinesses(businesses.map(b => b.businessId === businessId ? { ...b, isActive: !b.isActive } : b));
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to toggle business status: ${err.message}`;
      console.error(`[ToggleBusinessActive] Error for businessId: ${businessId}`, err);
      if (err.response?.status === 404) {
        setError(`Business with ID ${businessId} not found. Refreshing business list...`);
        await refreshBusinesses();
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleDepositAction = async (id, transactionId, approved) => {
    try {
      setActionLoading(true);
      if (!['businesses', 'users'].includes(entityType)) {
        throw new Error(`Invalid entityType: ${entityType}`);
      }
      const endpoint = entityType === 'businesses' ? '/admin/verify-business-deposit' : '/admin/verify-deposit';
      const payload = entityType === 'businesses'
        ? { businessId: id, transactionId, approved }
        : { userId: id, transactionId, approved };
      console.log(`[Deposit Action] Sending to ${endpoint}`, payload);
      const response = await axiosInstance.post(endpoint, payload);
      console.log(`[Deposit Action] Success for ${id}`, response.data);
      setDeposits(deposits.filter(d => !(d.id === id && d.transactionId === transactionId)));
      const statKey = `pending${entityType === 'businesses' ? 'Business' : 'User'}DepositsCount`;
      setStats(prev => ({
        ...prev,
        [statKey]: Math.max(0, prev[statKey] - 1),
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to ${approved ? 'approve' : 'reject'} deposit: ${err.message}`;
      console.error(`[Deposit Action] Error for ${id}`, err);
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdrawalAction = async (id, businessId, index, approved) => {
    try {
      setActionLoading(true);
      if (!['businesses', 'users'].includes(entityType)) {
        throw new Error(`Invalid entityType: ${entityType}`);
      }
      const endpoint = entityType === 'businesses' ? '/admin/verify-business-withdrawal' : '/admin/verify-withdrawal';
      const payload = entityType === 'businesses'
        ? { businessId: businessId || id, withdrawalIndex: index, approved }
        : { userId: id, withdrawalIndex: index, approved };
      console.log(`[Withdrawal Action] Sending to ${endpoint}`, payload);
      const response = await axiosInstance.post(endpoint, payload);
      console.log(`[Withdrawal Action] Success for ${businessId || id}`, response.data);
      setWithdrawals(withdrawals.filter(w => !(w.businessId === (businessId || id) && w.index === index)));
      const statKey = `pending${entityType === 'businesses' ? 'Business' : 'User'}WithdrawalsCount`;
      setStats(prev => ({
        ...prev,
        [statKey]: Math.max(0, prev[statKey] - 1),
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to ${approved ? 'approve' : 'reject'} withdrawal: ${err.message}`;
      console.error(`[Withdrawal Action] Error for ${businessId || id}`, err);
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const downloadCSV = (data, filename) => {
    if (!data?.length) {
      setError('No data to export.');
      return;
    }
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(val => `"${val ?? 'N/A'}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportUsers = () => {
    const csvData = users.map(({ username, _id, phoneNumber, kycStatus, isActive }) => ({
      username,
      id: _id,
      phoneNumber,
      kycStatus,
      isActive: isActive ? 'Yes' : 'No',
    }));
    downloadCSV(csvData, 'users.csv');
  };

  const exportBusinesses = () => {
    const csvData = businesses.map(({ name, businessId, phoneNumber, kycStatus, isActive }) => ({
      name,
      businessId,
      phoneNumber,
      kycStatus,
      isActive: isActive ? 'Yes' : 'No',
    }));
    downloadCSV(csvData, 'businesses.csv');
  };

  const exportDeposits = () => {
    const csvData = deposits.map(({ name, entityId, amount, currency, date, sourceOfFunds }) => ({
      name,
      entityId,
      amount: amount.toFixed(2),
      currency,
      date: new Date(date).toISOString(),
      sourceOfFunds,
    }));
    downloadCSV(csvData, `${entityType}_deposits.csv`);
  };

  const exportWithdrawals = () => {
    const csvData = withdrawals.map(({ name, entityId, amount, fee, currency, date, destination }) => ({
      name,
      entityId,
      amount: amount.toFixed(2),
      fee: fee.toFixed(2),
      currency,
      date: new Date(date).toISOString(),
      destinationType: destination.type,
      destinationDetails: destination.accountDetails,
    }));
    downloadCSV(csvData, `${entityType}_withdrawals.csv`);
  };

  const getErrorMessage = (err) => {
    if (err.code === 'ECONNABORTED') return 'Network timeout. Please check your connection.';
    if (err.response?.status === 401) {
      setTimeout(() => {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        window.location.href = '/signin';
      }, 5000);
      return 'Authentication failed. Retrying in 5 seconds...';
    }
    if (err.response?.status === 403) {
      return `Access denied: ${err.response?.data?.error || 'Admin access required.'}`;
    }
    if (err.response?.status === 404) return 'No data found.';
    if (err.response?.status === 500) return `Server error: ${err.response?.data?.details || 'Unknown'}`;
    return err.response?.data?.error || `Failed to load data: ${err.message}`;
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-panel">
        <h1 className="admin-title">FugiPay Admin Panel</h1>
        <p className="error-message">Please log in to access the admin panel.</p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h1 className="admin-title">FugiPay Admin Panel</h1>
      <div className="tabs">
        <button className={`tab-button ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
          Dashboard
        </button>
        <button className={`tab-button ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          Users
        </button>
        <button className={`tab-button ${tab === 'businesses' ? 'active' : ''}`} onClick={() => setTab('businesses')}>
          Businesses
        </button>
        <button className={`tab-button ${tab === 'deposits' ? 'active' : ''}`} onClick={() => setTab('deposits')}>
          Deposits
        </button>
        <button className={`tab-button ${tab === 'withdrawals' ? 'active' : ''}`} onClick={() => setTab('withdrawals')}>
          Withdrawals
        </button>
        <button
          className="export-button"
          style={{ marginLeft: 'auto' }}
          onClick={() => {
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            window.location.href = '/signin';
          }}
        >
          Logout
        </button>
      </div>
      {(tab === 'deposits' || tab === 'withdrawals') && (
        <div className="tabs">
          <button
            className={`tab-button ${entityType === 'businesses' ? 'active' : ''}`}
            onClick={() => setEntityType('businesses')}
          >
            Businesses
          </button>
          <button
            className={`tab-button ${entityType === 'users' ? 'active' : ''}`}
            onClick={() => setEntityType('users')}
          >
            Users
          </button>
        </div>
      )}
      {error && (
        <div className="error-message" style={{ color: '#D32F2F', display: 'flex', alignItems: 'center' }}>
          <span>{error}</span>
          {error.includes('Retrying') && <span style={{ marginLeft: '10px' }}>🔄</span>}
          <button className="export-button" onClick={() => {
            setError('');
            setLoading(true);
            setTimeout(() => {
              if (tab === 'dashboard') {
                fetchWithRetry('/admin/stats')
                  .then(({ data }) => {
                    console.log('[Retry Dashboard] Stats Response:', data);
                    setStats({
                      totalUsers: Number(data.totalUsers) || 0,
                      totalUserBalance: Number(data.totalUserBalance) || 0,
                      pendingUserDepositsCount: Number(data.pendingUserDepositsCount) || 0,
                      pendingUserWithdrawalsCount: Number(data.pendingUserWithdrawalsCount) || 0,
                      totalBusinesses: Number(data.totalBusinesses) || 0,
                      totalBusinessBalance: Number(data.totalBusinessBalance) || 0,
                      pendingBusinessDepositsCount: Number(data.pendingBusinessDepositsCount) || 0,
                      pendingBusinessWithdrawalsCount: Number(data.pendingBusinessWithdrawalsCount) || 0,
                      totalBalance: Number(data.totalBalance) || 0,
                      recentTxCount: Number(data.recentTxCount) || 0,
                    });
                  })
                  .catch(err => setError(getErrorMessage(err)))
                  .finally(() => setLoading(false));
              } else if (tab === 'users') {
                axiosInstance.get('/admin/users', { params: { page, limit: 10, search } })
                  .then(({ data }) => {
                    console.log('[Retry Users] Response:', data);
                    setUsers(Array.isArray(data.users) ? data.users : []);
                    setTotalPages(Math.ceil(data.total / 10) || 1);
                  })
                  .catch(err => setError(getErrorMessage(err)))
                  .finally(() => setLoading(false));
              } else if (tab === 'businesses') {
                axiosInstance.get('/admin/businesses', { params: { page, limit: 10, search } })
                  .then(({ data }) => {
                    console.log('[Retry Businesses] Response:', data);
                    setBusinesses(Array.isArray(data.businesses) ? data.businesses : []);
                    setTotalPages(data.pages || 1);
                  })
                  .catch(err => setError(getErrorMessage(err)))
                  .finally(() => setLoading(false));
              } else if (tab === 'deposits' || tab === 'withdrawals') {
                const endpoint = entityType === 'businesses' ? '/admin/businesses' : '/admin/users';
                axiosInstance.get(endpoint, { params: { page, limit: 10, search, isActive: true } })
                  .then(({ data }) => {
                    console.log(`[Retry ${entityType}] Response:`, data);
                    const entities = entityType === 'businesses' ? data.businesses : data.users;
                    if (!Array.isArray(entities)) {
                      setError(`Invalid ${entityType} response structure`);
                      return;
                    }
                    if (tab === 'deposits') {
                      const depositRequests = entities.flatMap(e => e.pendingDeposits?.map((d, i) => ({
                        id: e._id,
                        businessId: entityType === 'businesses' ? e.businessId : null,
                        transactionId: d.transactionId,
                        index: i,
                        name: entityType === 'businesses' ? e.name : e.username,
                        entityId: entityType === 'businesses' ? e.businessId : e.username,
                        amount: typeof d.amount === 'object' && d.amount.$numberDecimal
                          ? parseFloat(d.amount.$numberDecimal)
                          : parseFloat(d.amount?.toString() || '0'),
                        currency: d.currency || 'ZMW',
                        status: d.status?.trim().toLowerCase() || 'pending',
                        date: d.date || new Date().toISOString(),
                        sourceOfFunds: d.sourceOfFunds || 'N/A',
                      })).filter(d => d && d.status === 'pending') || []);
                      setDeposits(depositRequests);
                      setTotalPages(Math.ceil(depositRequests.length / 10) || 1);
                      if (!depositRequests.length) setError(`No pending ${entityType} deposit requests available.`);
                    } else {
                      const withdrawalRequests = entities.flatMap(e => e.pendingWithdrawals?.map((w, i) => ({
                        id: e._id,
                        businessId: entityType === 'businesses' ? e.businessId : null,
                        index: i,
                        name: entityType === 'businesses' ? e.name : e.username,
                        entityId: entityType === 'businesses' ? e.businessId : e.username,
                        amount: typeof w.amount === 'object' && w.amount.$numberDecimal
                          ? parseFloat(w.amount.$numberDecimal)
                          : parseFloat(w.amount?.toString() || '0'),
                        fee: entityType === 'businesses'
                          ? (typeof w.fee === 'object' && w.fee.$numberDecimal
                              ? parseFloat(w.fee.$numberDecimal)
                              : parseFloat(w.fee?.toString() || '0'))
                          : 0,
                        currency: entityType === 'businesses' ? (w.currency || 'ZMW') : 'ZMW',
                        status: w.status?.trim().toLowerCase() || 'pending',
                        date: w.date || new Date().toISOString(),
                        destination: entityType === 'businesses' ? {
                          type: w.destination?.type || 'unknown',
                          accountDetails: (
                            w.destination?.type === 'bank'
                              ? `${w.destination?.bankName || 'N/A'} (${w.destination?.accountNumber || 'N/A'}) ${w.destination?.swiftCode || ''}`
                              : w.destination?.accountNumber || 'N/A'
                          ).trim(),
                        } : { type: 'unknown', accountDetails: 'N/A' },
                      })).filter(w => w && w.status === 'pending') || []);
                      setWithdrawals(withdrawalRequests);
                      setTotalPages(Math.ceil(withdrawalRequests.length / 10) || 1);
                      if (!withdrawalRequests.length) setError(`No pending ${entityType} withdrawal requests available.`);
                    }
                  })
                  .catch(err => setError(getErrorMessage(err)))
                  .finally(() => setLoading(false));
              }
            }, 1000);
          }}>
            Retry
          </button>
        </div>
      )}
      {loading && <p className="error-message">Loading...</p>}
      {tab === 'dashboard' && !loading && (
        <div className="card-grid">
          <div className="card"><h3>Total Users</h3><p>{stats.totalUsers}</p></div>
          <div className="card"><h3>Total User Balance (ZMW)</h3><p>{stats.totalUserBalance.toFixed(2)}</p></div>
          <div className="card"><h3>Pending User Deposits</h3><p>{stats.pendingUserDepositsCount}</p></div>
          <div className="card"><h3>Pending User Withdrawals</h3><p>{stats.pendingUserWithdrawalsCount}</p></div>
          <div className="card"><h3>Total Businesses</h3><p>{stats.totalBusinesses}</p></div>
          <div className="card"><h3>Total Business Balance (ZMW)</h3><p>{stats.totalBusinessBalance.toFixed(2)}</p></div>
          <div className="card"><h3>Pending Business Deposits</h3><p>{stats.pendingBusinessDepositsCount}</p></div>
          <div className="card"><h3>Pending Business Withdrawals</h3><p>{stats.pendingBusinessWithdrawalsCount}</p></div>
          <div className="card"><h3>Total System Balance (ZMW)</h3><p>{stats.totalBalance.toFixed(2)}</p></div>
          <div className="card"><h3>Recent Transactions (24h)</h3><p>{stats.recentTxCount}</p></div>
        </div>
      )}
      {tab === 'users' && !loading && (
        <div>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by username or phone..."
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
            <button className="export-button" onClick={exportUsers}>Export Users (CSV)</button>
          </div>
          <div className="card-grid">
            {users.map(user => (
              <div key={user._id} className="card">
                <h3>{user.username}</h3>
                <p>Phone: {user.phoneNumber}</p>
                <p>KYC: {user.kycStatus}</p>
                <p>Active: {user.isActive ? 'Yes' : 'No'}</p>
                <select
                  value={user.kycStatus}
                  onChange={(e) => updateUserKYC(user._id, e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button className="action-button" onClick={() => toggleUserActive(user._id)}>
                  {user.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            ))}
          </div>
          <div className="search-bar">
            <button className="export-button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button className="export-button" onClick={() => setPage(p => p < totalPages ? p + 1 : p)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      )}
      {tab === 'businesses' && !loading && (
        <div>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by business name or ID..."
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
            <button className="export-button" onClick={exportBusinesses}>Export Businesses (CSV)</button>
          </div>
          <div className="card-grid">
            {businesses.map(business => (
              <div key={business.businessId} className="card">
                <h3>{business.name}</h3>
                <p>ID: {business.businessId}</p>
                <p>Phone: {business.phoneNumber}</p>
                <p>KYC: {business.kycStatus}</p>
                <p>Active: {business.isActive ? 'Yes' : 'No'}</p>
                <select
                  value={business.kycStatus}
                  onChange={(e) => updateBusinessKYC(business.businessId, e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button className="action-button" onClick={() => toggleBusinessActive(business.businessId)}>
                  {business.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            ))}
          </div>
          <div className="search-bar">
            <button className="export-button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button className="export-button" onClick={() => setPage(p => p < totalPages ? p + 1 : p)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      )}
      {tab === 'deposits' && !loading && (
        <div>
          <div className="search-bar">
            <input
              type="text"
              placeholder={`Search by ${entityType === 'businesses' ? 'business name or ID' : 'username or phone'}...`}
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
            <button className="export-button" onClick={exportDeposits}>Export Deposits (CSV)</button>
          </div>
          <div className="card-grid">
            {deposits.map((deposit, idx) => (
              <div key={`${deposit.id}-${deposit.transactionId}-${idx}`} className="card">
                <h3>{deposit.name}</h3>
                <p>ID: {deposit.entityId}</p>
                <p>Amount: {deposit.amount.toFixed(2)} {deposit.currency}</p>
                <p>Source: {deposit.sourceOfFunds}</p>
                <p>Date: {new Date(deposit.date).toLocaleString()}</p>
                <p>Status: {deposit.status}</p>
                <button
                  className="action-button"
                  onClick={() => handleDepositAction(deposit.id, deposit.transactionId, true)}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : 'Approve'}
                </button>
                <button
                  className="action-button"
                  onClick={() => handleDepositAction(deposit.id, deposit.transactionId, false)}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : 'Reject'}
                </button>
              </div>
            ))}
          </div>
          <div className="search-bar">
            <button className="export-button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button className="export-button" onClick={() => setPage(p => p < totalPages ? p + 1 : p)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      )}
      {tab === 'withdrawals' && !loading && (
        <div>
          <div className="search-bar">
            <input
              type="text"
              placeholder={`Search by ${entityType === 'businesses' ? 'business name or ID' : 'username or phone'}...`}
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
            <button className="export-button" onClick={exportWithdrawals}>Export Withdrawals (CSV)</button>
          </div>
          <div className="card-grid">
            {withdrawals.map((withdrawal, idx) => (
              <div key={`${withdrawal.id}-${withdrawal.index}-${idx}`} className="card">
                <h3>{withdrawal.name}</h3>
                <p>ID: {withdrawal.entityId}</p>
                <p>Amount: {withdrawal.amount.toFixed(2)} {withdrawal.currency}</p>
                <p>Fee: {withdrawal.fee.toFixed(2)} {withdrawal.currency}</p>
                <p>Destination: {withdrawal.destination?.type || 'N/A'} - {withdrawal.destination?.accountDetails || 'N/A'}</p>
                <p>Date: {new Date(withdrawal.date).toLocaleString()}</p>
                <p>Status: {withdrawal.status}</p>
                <button
                  className="action-button"
                  onClick={() => handleWithdrawalAction(withdrawal.id, withdrawal.businessId, withdrawal.index, true)}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : 'Approve'}
                </button>
                <button
                  className="action-button"
                  onClick={() => handleWithdrawalAction(withdrawal.id, withdrawal.businessId, withdrawal.index, false)}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Processing...' : 'Reject'}
                </button>
              </div>
            ))}
          </div>
          <div className="search-bar">
            <button className="export-button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button className="export-button" onClick={() => setPage(p => p < totalPages ? p + 1 : p)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;